// Pure logic for the Roles admin page (App.jsx's RolesPage, via
// admin/pages/RolesPage.jsx) — extracted so it's unit-testable with
// node --test (no React harness available in this codebase), same
// convention as toAddressAutoSelect.js/partyName.js.

// Fixed display order for the Role column/sort — NOT alphabetical (a csr
// account is lowercase by convention; sorting it alongside "Admin"
// alphabetically would put it first, which reads wrong for a privilege
// hierarchy). An agent whose lastKnownRole is null (never resolved to a
// recognized role — see server's highestPriorityRole()) sorts last, after csr.
// Only admin and csr remain — the senior role was retired 2026-09-15.
const ROLE_ORDER = ['admin', 'csr']

export function roleRank(role) {
  const idx = ROLE_ORDER.indexOf(role)
  return idx === -1 ? ROLE_ORDER.length : idx
}

export const ROLE_LABELS = { admin: 'Admin', csr: 'csr' }

export function roleLabel(role) {
  return ROLE_LABELS[role] ?? 'Unknown'
}

// Agent display name — first name for the alphabetical "Agent" sort (per
// spec: "Agent alphabetically by first name"), falling back through
// displayName -> username -> agentId so a never-fully-resolved row still
// sorts somewhere sensible rather than crashing.
export function agentSortName(agent) {
  const name = agent.displayName || agent.username || agent.agentId || ''
  return name.trim().split(/\s+/)[0]?.toLowerCase() ?? ''
}

// column: 'agent' | 'role' | one of TOGGLE_FIELDS. direction: 'asc' | 'desc'.
// Stable-ish (Array.prototype.sort in V8 is stable) so equal keys keep their
// prior relative order rather than shuffling on every re-sort.
export function sortAgents(agents, column, direction = 'asc') {
  const dir = direction === 'desc' ? -1 : 1
  const sorted = [...agents]
  sorted.sort((a, b) => {
    if (column === 'agent') {
      return agentSortName(a).localeCompare(agentSortName(b)) * dir
    }
    if (column === 'role') {
      // Secondary tiebreak: alphabetical by first name within a role — NOT
      // multiplied by dir, so toggling the Role direction reorders the role
      // groups themselves without reversing the name order inside each one.
      const roleCmp = (roleRank(a.lastKnownRole) - roleRank(b.lastKnownRole)) * dir
      if (roleCmp !== 0) return roleCmp
      return agentSortName(a).localeCompare(agentSortName(b))
    }
    // A toggle column: false < true, so ascending shows disabled first.
    const cmp = (a[column] === b[column]) ? 0 : (a[column] ? 1 : -1)
    return cmp * dir
  })
  return sorted
}

// Bulk-selection helpers — "select all" and "select by role/toggle-state
// criteria" both reduce to picking a subset of agentIds.

export function selectAllAgentIds(agents) {
  return agents.map((a) => a.agentId)
}

// Behind the Roles page's single "select agents where role is [Any] and
// [Any toggle] is [enabled/disabled]" row — role === null matches every
// role, and field === null OR state === null matches every toggle state
// (both must be set together — "state" alone is meaningless without a
// column to read it from). Passing just one of role/field+state reproduces
// what were previously two separate functions (selectAgentIdsByRole/
// selectAgentIdsByToggleState, since removed — this one function covers
// role-only, state-only, and role+state combined).
export function selectAgentIdsByFilter(agents, { role = null, field = null, state = null } = {}) {
  return agents
    .filter((a) => role == null || a.lastKnownRole === role)
    .filter((a) => field == null || state == null || Boolean(a[field]) === state)
    .map((a) => a.agentId)
}

// Client-side mirror of server/src/routes/admin-roles.js's canEditTargetAgent()
// — display only (the server is the real enforcement; a stale/tampered
// client computing this differently just means a write it attempts gets
// refused server-side, not a security hole). `viewer` is /api/me's own
// { agentId, supportPermissions } plus whether the viewer is admin
// (elevated); `agent` is one row from the fetched list.
//
// Admin viewers may edit every row, unaffected. A support-permission
// csr viewer may edit only their own row, or another row whose CURRENT
// lastKnownRole is 'csr' and whose CURRENT supportPermissions is false —
// including granting Support Permissions to that eligible csr (false -> true
// is just another field change on a row they're otherwise allowed to touch).
// Every other row (an admin, or a csr who already holds Support
// Permissions) is not editable by this viewer.
export function canEditAgentRow(viewer, agent) {
  if (viewer.elevated) return true
  if (!viewer.supportPermissions) return false
  if (agent.agentId === viewer.agentId) return true
  return agent.lastKnownRole === 'csr' && agent.supportPermissions === false
}

// A "former" agent: the Entra sync last found them in NO role group
// (entra_active === false — removed from Entra, row kept for history; see
// repositories/agent-permissions.js's syncAgentsFromEntra). null/undefined
// means the sync has never judged this row (sync off, or a pre-sync row), so
// it is NOT former. The Roles page hides former agents by default behind a
// "Show former agents" button so the table does not fill up with people who
// no longer work the floor.
export function isFormerAgent(agent) {
  return agent?.entraActive === false
}

// The bulk action's own "flip everyone selected to the other state" mode —
// only meaningful when every selected agent currently shares ONE state for
// this field (that's what "select by a column's current state" produces).
// Returns null (nothing to flip) for an empty selection rather than
// guessing a direction.
export function oppositeState(agents, agentIds, field) {
  if (!agentIds.length) return null
  const selected = new Set(agentIds)
  const current = agents.filter((a) => selected.has(a.agentId)).map((a) => Boolean(a[field]))
  const allOn = current.every((v) => v === true)
  const allOff = current.every((v) => v === false)
  if (allOn) return false
  if (allOff) return true
  // Mixed state (a manually-adjusted selection, not one of the three preset
  // filters) — default to turning everyone ON, the more common bulk intent
  // (enabling a newly-cleared flow for a cohort), rather than refusing.
  return true
}
